import {
  type DragEndEvent,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import {
  type Table as DataTableInstance,
  type ExpandedState,
  type InitialTableState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect } from "react";

import type {
  WpxTableFeature,
  WpxTableFunctions,
  WpxTableHideConfig,
  WpxTablePropsClassNames,
  WpxTableUiConfig,
} from "../types/dataTable.types";
import { onWPXColumnSizingChange } from "../utils/tableUtils";

import { useDataTablePresentationState } from "./useDataTablePresentationState";
import { useDataTableServerState } from "./useDataTableServerState";
import { useWpxTableColumnState } from "./useWpxTableColumnState";
import { useWpxTableUiResolution } from "./useWpxTableUiResolution";

import { TWENTY_FIVE } from "@/app/constants/constants";
import {
  ACTIVE_TAB,
  DEFAULT_COLUMN_SIZE,
} from "@/app/constants/global-constants";
import { CMouseSensor } from "@/components/dnd/Sensor";
import type { ResponsePagination } from "@/components/pagination/hooks/usePagination";
import { useTheme } from "@/components/ThemeProvider";
import { useIsMobile } from "@/hooks/browser/useIsMobile";
import useMeasure from "@/hooks/dom/useMeasure";
import useQueryParams from "@/hooks/query/useQueryParams";
import type { ExtendedColumnDef } from "@/interfaces/globals";

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

const areOrderedColumnsEqual = (
  leftColumnOrder: string[],
  rightColumnOrder: string[],
): boolean => {
  if (leftColumnOrder.length !== rightColumnOrder.length) {
    return false;
  }

  return leftColumnOrder.every((columnId, columnIndex) => {
    return columnId === rightColumnOrder[columnIndex];
  });
};

const hasToolbarContent = <TData, TValue>(
  toolbar: WpxTableFunctions<TData, TValue>,
): boolean => {
  return Boolean(
    toolbar.isExport ||
    toolbar.search ||
    toolbar.columnToggle ||
    toolbar.add ||
    toolbar.custom ||
    toolbar.floatingMenu,
  );
};

// ---------------------------------------------------------------------------
// Params & return types
// ---------------------------------------------------------------------------

interface UseWpxTableSetupParams<TData, TValue> {
  title: string;
  rows?: TData[];
  columns: ExtendedColumnDef<TData, TValue>[];
  toolbar?: WpxTableFunctions<TData, TValue>;
  pagination?: ResponsePagination;
  feature?: WpxTableFeature;
  ui?: WpxTableUiConfig;
  hide?: WpxTableHideConfig;
  filterClearButtonSize?: "sm" | "lg";
  classNames?: WpxTablePropsClassNames;
  enableColumnResizing?: boolean;
  isLoading?: boolean;
  initialState?: InitialTableState;
  showTitle?: boolean;
  showSelectedCount?: boolean;
  showTotalCount?: boolean;
  enableEnhancedUX?: boolean;
  columnDraggable?: boolean;
  isTablePaginated?: boolean;
  onTableInstanceChange?: (instance: DataTableInstance<TData>) => void;
  priorityColumnIds?: string[];
  scopeByActiveTab?: boolean;
}

interface UseWpxTableSetupReturn<TData, TValue> {
  columnOrder: string[];
  currentPage?: number;
  filterOperation: ReturnType<typeof useQueryParams>;
  filteredColumns: ExtendedColumnDef<TData, TValue>[];
  handleDragEnd: (event: DragEndEvent) => void;
  handlePageChange: (
    pageNumber: number,
    setClientPageIndex?: (pageIndex: number) => void,
  ) => void;
  handlePageSizeChange?: (pageSize: number) => void;
  hasColumnReordered: boolean;
  hasToolbarActions: boolean;
  isColumnDraggable: boolean;
  isEnhancedUxEnabled: boolean;
  isLoading: boolean;
  isMobile: boolean;
  pageSize: number;
  ref: React.RefObject<HTMLTableSectionElement>;
  resetColumnOrder: () => void;
  resolvedPagination?: ResponsePagination;
  resolvedRows: TData[];
  resolvedUi: {
    classNames?: WpxTablePropsClassNames;
    clearButtonSize?: "sm" | "lg";
    enableColumnResizing: boolean;
    hideFilters: boolean;
    hideToolbar: boolean;
    isLoading: boolean;
    showSelectedCount: boolean;
    showTitle: boolean;
    showTotalCount: boolean;
  };
  sensors: ReturnType<typeof useSensors>;
  serverTotalResult: number;
  shouldRenderPaginator: boolean;
  skin: string;
  table: DataTableInstance<TData>;
  toolbar?: WpxTableFunctions<TData, TValue>;
  totalPages?: number;
  totalResultCount: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Thin orchestrator that composes UI resolution, column state, server
 * pagination, and the TanStack table instance into a single return shape
 * consumed by `WpxTable`.
 *
 * @param {UseWpxTableSetupParams<TData, TValue>} params - Table setup inputs forwarded from `WpxTable` props.
 * @returns {UseWpxTableSetupReturn<TData, TValue>} Flat object with the table instance, resolved configs, and handlers.
 */
function useWpxTableSetup<TData extends { id?: string }, TValue>(
  params: UseWpxTableSetupParams<TData, TValue>,
): UseWpxTableSetupReturn<TData, TValue> {
  const {
    title,
    rows,
    columns,
    toolbar,
    pagination,
    feature,
    ui,
    hide,
    filterClearButtonSize,
    classNames,
    enableColumnResizing = false,
    isLoading: isLoadingProp = false,
    initialState,
    showTitle = false,
    showSelectedCount,
    showTotalCount,
    enableEnhancedUX,
    columnDraggable,
    isTablePaginated = true,
    onTableInstanceChange,
    priorityColumnIds = [],
    scopeByActiveTab = true,
  } = params;

  // -- UI resolution --------------------------------------------------------

  const { featureConfig, isLoading, resolvedUi } = useWpxTableUiResolution({
    classNames,
    enableColumnResizing,
    feature,
    filterClearButtonSize,
    hideFilters: hide?.filters,
    hideFunctions: hide?.functions,
    isLoading: isLoadingProp,
    showSelectedCount,
    showTitle,
    showTotalCount,
    toolbarHide: toolbar?.hide,
    ui,
  });

  const isColumnDraggable = columnDraggable ?? featureConfig.columnDraggable;

  const isEnhancedUxEnabled =
    enableEnhancedUX ?? featureConfig.enableEnhancedUX;

  // -- Active tab (URL-driven scope for column order + row selection) ------

  const filterOperation = useQueryParams();
  const activeTab = scopeByActiveTab
    ? (filterOperation.searchParams.get(ACTIVE_TAB) ?? undefined)
    : undefined;

  // -- Column state ---------------------------------------------------------

  const {
    columnFilters,
    columnOrder,
    columnPinning,
    columnSizing,
    columnVisibilityState,
    defaultColumnOrder,
    filteredColumns,
    resetColumnOrder,
    setColumnFilters,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setColumnVisibility,
    setSorting,
    sorting,
    tableColumns,
  } = useWpxTableColumnState({
    activeTab,
    columns,
    isColumnDraggable,
    isLoading,
    loaderClassName: resolvedUi.classNames?.loaderClassName,
    priorityColumnIds,
    title,
  });

  // -- Rows, pagination, & presentation -------------------------------------

  const resolvedRows = rows ?? [];
  const isMobile = useIsMobile();
  const { ref } = useMeasure<HTMLTableSectionElement>();
  const { theme: skin } = useTheme();

  const {
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    placeholderLimit,
    resolvedPagination,
    shouldRenderPaginator,
    tablePaginationOptions,
    tablePaginationState,
    totalPages,
  } = useDataTableServerState({
    dataLength: resolvedRows.length,
    initialState,
    isTablePaginated,
    pagination,
    legacyPagination: toolbar?.pagination,
  });

  const { tableData } = useDataTablePresentationState({
    data: resolvedRows,
    isLoading,
    placeholderLimit,
    title,
  });

  // -- Row expansion --------------------------------------------------------

  const [expanded, setExpanded] = React.useState<Record<string, boolean>>(
    toolbar?.initialExpanded || {},
  );

  const [rowSelection, setRowSelection] = React.useState({});

  const previousActiveTabRef = React.useRef(activeTab);
  useEffect(() => {
    if (previousActiveTabRef.current === activeTab) {
      return;
    }
    previousActiveTabRef.current = activeTab;
    setRowSelection({});
  }, [activeTab]);

  // -- DnD sensors ----------------------------------------------------------

  const sensors = useSensors(
    useSensor(CMouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  // -- Table instance -------------------------------------------------------

  const table = useReactTable({
    getRowId: (row, index): string => row.id ?? index.toString(),
    data: tableData,
    columns: tableColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: onWPXColumnSizingChange(setColumnSizing),
    columnResizeMode: "onChange",
    enableColumnResizing: resolvedUi.enableColumnResizing,
    onExpandedChange: (updaterOrValue) => {
      const nextExpandedState =
        typeof updaterOrValue === "function"
          ? updaterOrValue(expanded as ExpandedState)
          : updaterOrValue;
      const expandedRecord = nextExpandedState as Record<string, boolean>;

      setExpanded(expandedRecord);
      toolbar?.onExpandedChange?.(expandedRecord);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(toolbar?.enableRowExpansion && {
      getExpandedRowModel: getExpandedRowModel(),
    }),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnOrder: isColumnDraggable ? columnOrder : defaultColumnOrder,
      columnFilters,
      columnPinning,
      columnSizing,
      columnVisibility: columnVisibilityState,
      rowSelection,
      ...(tablePaginationState && {
        pagination: tablePaginationState,
      }),
      ...(toolbar?.enableRowExpansion && {
        expanded: expanded as ExpandedState,
      }),
    },
    onColumnOrderChange: setColumnOrder,
    defaultColumn: {
      minSize: 0,
      size: resolvedUi.enableColumnResizing ? DEFAULT_COLUMN_SIZE : undefined,
      maxSize: Number.MAX_SAFE_INTEGER,
      enableSorting: false,
    },
    ...tablePaginationOptions,
  });

  // -- Derived values -------------------------------------------------------

  const hasToolbarActions = React.useMemo(() => {
    return toolbar ? hasToolbarContent(toolbar) : false;
  }, [toolbar]);

  const hasColumnReordered = React.useMemo(() => {
    if (!isColumnDraggable) {
      return false;
    }

    return !areOrderedColumnsEqual(defaultColumnOrder, columnOrder);
  }, [columnOrder, defaultColumnOrder, isColumnDraggable]);

  const totalResultCount =
    resolvedPagination?.totalResult ?? resolvedRows.length;
  const serverTotalResult = resolvedPagination?.totalResult ?? 0;

  // -- Effects --------------------------------------------------------------

  useEffect(() => {
    toolbar?.multiFloating?.handleAddTable(title, table);
    onTableInstanceChange?.(table);
  }, [onTableInstanceChange, toolbar?.multiFloating, table, title]);

  // -- DnD handler ----------------------------------------------------------

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeColumn = table.getColumn(activeId);
    const overColumn = table.getColumn(overId);

    if (activeColumn?.getIsPinned() || overColumn?.getIsPinned()) {
      return;
    }

    setColumnOrder((previousColumnOrder) => {
      const oldIndex = previousColumnOrder.indexOf(activeId);
      const newIndex = previousColumnOrder.indexOf(overId);

      if (oldIndex === -1 || newIndex === -1) {
        return previousColumnOrder;
      }

      return arrayMove(previousColumnOrder, oldIndex, newIndex);
    });
  };

  // -- Return ---------------------------------------------------------------

  return {
    table,
    filterOperation,
    isLoading,
    isColumnDraggable,
    isEnhancedUxEnabled,
    filteredColumns,
    columnOrder,
    toolbar,
    resolvedRows,
    resolvedUi,
    resolvedPagination,
    ref,
    skin,
    isMobile,
    currentPage,
    pageSize: tablePaginationState?.pageSize ?? TWENTY_FIVE,
    handlePageChange,
    handlePageSizeChange,
    shouldRenderPaginator,
    totalPages,
    hasToolbarActions,
    hasColumnReordered,
    totalResultCount,
    serverTotalResult,
    resetColumnOrder,
    handleDragEnd,
    sensors,
  };
}

export { useWpxTableSetup };
export type { UseWpxTableSetupParams, UseWpxTableSetupReturn };
